<template>
  <li v-if="shouldShowItem" :class="{'bg-gray-50': item.is_primary && item.similar_articles?.length}">
    <div class="block hover:bg-gray-50">
      <div class="flex items-center px-4 py-4 sm:px-6">
        <div class="flex min-w-0 flex-1 items-center">
          <div class="flex-shrink-0">
            <input
              type="checkbox"
              :checked="isSelected"
              @click="(event) => $emit('toggle-selection', event)"
              class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
          </div>
          <div class="min-w-0 flex-1 px-4">
            <div>
              <div class="flex items-center">
                <p class="truncate text-sm font-medium text-indigo-600">{{ item.title }}</p>
                <span v-if="item.is_important" class="ml-2 inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                  Important
                </span>
              </div>
              
              <!-- Activism relevance badge - more visible -->
              <div v-if="item.analyzed" class="mt-1">
                <span
                  class="inline-flex rounded-full px-2 py-1 text-xs font-semibold"
                  :class="{
                    'bg-red-100 text-red-800': activismRelevance === 'visoka',
                    'bg-yellow-100 text-yellow-800': activismRelevance === 'srednja',
                    'bg-gray-100 text-gray-800': !activismRelevance || activismRelevance === 'niska'
                  }"
                >
                  Relevance: {{ activismRelevance || 'niska' }}
                </span>
              </div>
            </div>
            <div class="mt-2 flex text-sm text-gray-500">
              <span class="truncate">{{ item.source_name }}</span>
              <span class="ml-1 flex-shrink-0">· {{ item.language }}</span>
              <span v-if="item.analyzed_at" class="ml-1 flex-shrink-0">· Analyzed: {{ formatDate(item.analyzed_at) }}</span>
            </div>
          </div>
        </div>
        <div>
          <button
            v-if="!item.analyzed"
            @click="$emit('analyze', item.id)"
            :disabled="analyzing === item.id"
            class="text-indigo-600 hover:text-indigo-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="analyzing === item.id">
              <svg class="animate-spin h-5 w-5 mr-2 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Analyzing...
            </span>
            <span v-else>Analyze</span>
          </button>
          <div v-else class="flex gap-2">
            <button
              @click="$emit('view-analysis', item)"
              class="button-secondary"
            >
              View Analysis
            </button>
            <button
              v-if="item.analyzed"
              @click="$emit('toggle-importance', item)"
              class="button-secondary ml-2"
              :class="{ 'bg-yellow-50 border-yellow-200': item.is_important }"
            >
              {{ item.is_important ? 'Remove Importance' : 'Mark as Important' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Similar articles section -->
    <div v-if="item.similar_articles?.length" class="mt-4 px-4 pb-4">
      <h4 class="text-sm font-medium text-gray-900">Similar Articles:</h4>
      <ul class="mt-2 space-y-2">
        <li v-for="articleId in item.similar_articles" :key="articleId" class="text-sm">
          <div class="flex justify-between">
            <span class="text-gray-600">{{ findArticle(articleId)?.title || 'Unknown Article' }}</span>
            <span v-if="item.similarity_scores && articleId in item.similarity_scores" class="text-gray-500">
              {{ Math.round(item.similarity_scores[articleId] * 100) }}% similar
            </span>
          </div>
        </li>
      </ul>
    </div>
  </li>
</template>

<script setup lang="ts">
import { formatDate } from '@/utils/date'
import type { Content } from '@/types/content'
import { computed, ref } from 'vue'
import type { Analysis } from '@/types/analysis'

const props = defineProps<{
  item: Content
  isSelected: boolean
  analyzing: string | null
  findArticle: (id: string) => Content | undefined
}>()

const emit = defineEmits<{
  (e: 'toggle-selection', event: MouseEvent): void
  (e: 'analyze', id: string): void
  (e: 'view-analysis', item: Content): void
  (e: 'toggle-importance', item: Content): void
}>()

// Get activism relevance from analysis
const activismRelevance = computed(() => {
  // Check for the top-level property first (guaranteed to be safe by Content type)
  if (props.item.activism_relevance) {
    console.log('Using top-level activism_relevance for item:', props.item.id, props.item.activism_relevance);
    return props.item.activism_relevance;
  }

  // If no analysis at all, return null or default
  if (!props.item.analysis) {
    console.log('No analysis data for item:', props.item.id);
    return null;
  }

  try {
    // Type-safe access to aktivizam_relevantnost (which definitely exists in the type)
    if (props.item.analysis.aktivizam_relevantnost) {
      console.log('Using analysis.aktivizam_relevantnost for item:', props.item.id, props.item.analysis.aktivizam_relevantnost);
      return props.item.analysis.aktivizam_relevantnost;
    }
    
    // Optional typescript field that we added
    if (props.item.analysis.activism_relevance) {
      console.log('Using analysis.activism_relevance for item:', props.item.id, props.item.analysis.activism_relevance);
      return props.item.analysis.activism_relevance;
    }
    
    console.log('No activism relevance found for item:', props.item.id);
    return null;
  } catch (e) {
    console.error('Error getting activism relevance:', e);
    return null;
  }
});

const shouldShowItem = computed(() => {
  const relevance = activismRelevance.value;
  console.log('ContentListItem - Checking visibility:', {
    itemId: props.item.id,
    title: props.item.title,
    relevance: relevance,
    analyzed: props.item.analyzed,
    rawAnalysis: props.item.analysis,
    shouldShow: true
  });
  
  // Show all items, regardless of relevance
  return true;
});
</script>

<style scoped>
.button-primary {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
  background-color: #4F46E5;
  border: 1px solid transparent;
  border-radius: 0.375rem;
}

.button-primary:hover {
  background-color: #4338CA;
}

.button-secondary {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #4F46E5;
  background-color: white;
  border: 1px solid #E5E7EB;
  border-radius: 0.375rem;
}

.button-secondary:hover {
  background-color: #F9FAFB;
}
</style> 